import debounce from 'lodash.debounce';

type Task = Promise<void>;
type LoaderState = boolean;

const DELAY_TIME = 1000; //ms

/**
 * @description module controlling the loader covering the capsule iframe
 * it receives various processes as input, and turns the loader on and off
 */
export class LoaderCtrl {
	private _taskPromises = new Set<Task>();
	private _state: LoaderState;
	private _isWorking: LoaderState = false;

	onSetupChange?: (isWorking: LoaderState) => void;
	onWorkingChange?: (isWorking: LoaderState) => void;

	constructor(initialState: LoaderState = true) {
		this._state = initialState;
	}

	//inputs:
	// keep an input per use case, for more flexability in the future
	handleWebpackRepacking = (bundlingPromise: Promise<void>) => {
		this.handleTaskAdded(bundlingPromise);
	};

	handleInstallFiles = (writePromise: Promise<void>) => {
		this.handleTaskAdded(writePromise);
	};

	handleExampleChanges = () => {
		this.updateWorkingLoader(true);
		this.updateSetupLoader(true);
	};

	reset = (loaderState: LoaderState = true) => {
		this._taskPromises = new Set<Task>();

		this.updateWorkingLoader(loaderState);
		this.updateSetupLoader(loaderState);
	};

	private handleTaskAdded(task: Task) {
		this._taskPromises.add(task);
		task.then(() => this.handleTaskComplete(task));

		this.reevaluate();
	}

	private handleTaskComplete(task: Task) {
		if (!this._taskPromises.has(task)) return;

		this._taskPromises.delete(task);

		this.reevaluate();
	}

	private reevaluate() {
		const isBusy: LoaderState = 0 < this._taskPromises.size;

		this.updateWorkingLoader(isBusy);

		if (isBusy === true) {
			this.delayedUpdateLoader.cancel();
		} else {
			this.delayedUpdateLoader(false);
		}
	}

	private updateSetupLoader(nextState: LoaderState) {
		const { onSetupChange, _state } = this;
		this.delayedUpdateLoader.cancel();

		if (_state === nextState) return;

		this._state = nextState;
		onSetupChange && onSetupChange(nextState);
	}

	private updateWorkingLoader(nextIsWorking: LoaderState) {
		const { onWorkingChange, _isWorking } = this;
		if (nextIsWorking === _isWorking) return;

		this._isWorking = nextIsWorking;
		onWorkingChange && onWorkingChange(nextIsWorking);
	}

	private delayedUpdateLoader = debounce(this.updateSetupLoader.bind(this), DELAY_TIME, {
		trailing: true,
	});
}

// //helper method, for debugging:
// let i = 0;
// function genUniq() {
// 	return i++;
// }
